import { useEffect, useState } from "react";

import { useTrack } from "@/hooks/useTrack/useTrack";
import { EditFilled } from "@aircall/icons";
import { Button, Flex, Link, Typography } from "@aircall/tractor";
import { MultiInlineSearchSelect } from "@dashboard/library";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useLinkTagsToCall } from "@/hooks/useLinkToTags/useLinkTagsToCall";
import {
  filterOutAdminPortalTags,
  filterOutSystemTags,
  transformTagData,
  useTags,
} from "@/hooks/useTags/useTags";
import { TagOption } from "@/hooks/useTags/useTags.decl";
import { SectionHeader } from "../SectionHeader";
import { SectionLayout } from "../SectionLayout";
import { TagsList } from "../TagList";
import { RouteParams } from "../Transcription/Transcription.decl";

import { MultiSelectTagItem } from "./MultiSelectTagItem";
import { TAG_PAGE_ROUTE } from "./SectionTags.constants";

import { RenderMultiple } from "@/components/RenderMultiple";
import { SkeletonTag } from "@/components/Skeleton";
import { useCall } from "@/hooks/useCall/useCall";
import { useGetLine } from "@/hooks/useGetLine/useGetLine";
import { useBaseStore } from "@/store/store";
import { isEqual, sortBy } from "lodash";

function SectionTagsBody() {
  const { t } = useTranslation();
  const { track } = useTrack();
  const { callId } = useParams<RouteParams>();

  const { call } = useCall();
  const { lineId } = useGetLine();
  const activeTags = useBaseStore((state) => state.activeTags);
  const tagIds = useBaseStore((state) => state.tagIds);
  const setTagIds = useBaseStore((state) => state.setTagIds);
  const isMutationLoading = useBaseStore((state) => state.mutationLoading);
  const companyTags = useBaseStore((state) => state.companyTags);

  const [displayTagSelector, setDisplayTagSelector] = useState(false);
  const [options, setOptions] = useState<TagOption[]>(() =>
    transformTagData(companyTags)
  );

  useEffect(() => {
    setTagIds(activeTags.map((tag) => tag.id));
  }, [setTagIds]);

  const { updateTags } = useLinkTagsToCall({ callId: callId! });

  const { loading: isLoading } = useTags();

  const handleOnClick = () => {
    setDisplayTagSelector(true);
  };

  const handleOnClose = () => {
    const hasDifferentTags = !isEqual(
      sortBy(activeTags.map((tag) => tag.id)),
      sortBy(tagIds)
    );

    if (hasDifferentTags) {
      updateTags(tagIds);
    }

    track("conversation_info_tag_updated", {
      call_uuid: call?.uuid,
      number_id: lineId,
      update_type: tagIds.length >= activeTags.length ? "added" : "deleted",
    });
    setDisplayTagSelector(false);
    setOptions(transformTagData(companyTags));
  };

  const handleOnSelect = (keys: string[]) => {
    setTagIds(keys);
  };
  const handleOnSearch = (query: string) => {
    if (!query) {
      return setOptions(transformTagData(companyTags));
    }

    setOptions(
      transformTagData(companyTags).filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  if (isLoading) {
    return (
      <RenderMultiple
        data-test="section-tags-loader"
        gap="xxs"
        direction="horizontal"
        quantity={2}
      >
        {(index) => <SkeletonTag key={index} />}
      </RenderMultiple>
    );
  }

  if (!companyTags.filter(filterOutAdminPortalTags).length) {
    return (
      <Typography
        variant="bodyRegularS"
        color="text-disabled"
        data-test="no-tag-added-text"
      >
        <Trans i18nKey="call_context.tag_page_message">
          You haven’t added tags yet. If you want to organize calls in category,
          create tags in the
          <Link
            href={TAG_PAGE_ROUTE}
            target="_blank"
            color="text-interactive-secondary"
            data-test="tag_page_link"
          />
          .
        </Trans>
      </Typography>
    );
  }

  if (displayTagSelector) {
    return (
      <MultiInlineSearchSelect
        defaultOpen
        loading={isMutationLoading}
        hideSelectedOptions={false}
        options={options}
        onSearch={handleOnSearch}
        onSelect={handleOnSelect}
        onClose={handleOnClose}
        selectedKeys={tagIds}
        renderItem={(option) => <MultiSelectTagItem option={option} />}
        renderTag={(option) => <MultiSelectTagItem option={option} clearable />}
      />
    );
  }

  const filteredActiveTags = activeTags.filter(filterOutSystemTags);

  if (!filteredActiveTags.length) {
    return (
      <Flex gap="s" alignItems="center">
        <Typography variant="bodyRegularS" color="text-disabled">
          {t("call_context.no_tags")}
        </Typography>
        <Button
          data-test="section-tags-edit-tags-button"
          onClick={handleOnClick}
          size="xSmall"
          mode="ghost"
          variant="secondary"
        >
          <EditFilled />
        </Button>
      </Flex>
    );
  }

  return (
    <Flex gap="s" alignItems="center">
      <TagsList tags={filteredActiveTags} />
      <Button
        data-test="section-tags-edit-tags-button"
        onClick={handleOnClick}
        size="xSmall"
        mode="ghost"
        variant="secondary"
      >
        <EditFilled />
      </Button>
    </Flex>
  );
}

export function SectionTags() {
  const { t } = useTranslation();

  return (
    <SectionLayout data-test="tags-section">
      <SectionHeader>{t("call_context.tags")}</SectionHeader>
      <SectionTagsBody />
    </SectionLayout>
  );
}
